import React, { useContext, useEffect } from 'react';
import * as R from 'ramda';
import { Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { MAuthDialog } from 'library';
import {
  isAuthenticated,
  signinRedirect,
  signinRedirectCallback,
  // signinSilentCallback,
} from 'utils/authService';
import { useUserRolePermissions } from 'hooks';
// HIDDEN REMOVE AT LATER DATE
import Hidden from 'feature/hidden/Hidden';
import ComponentPreview from 'feature/component-preview/Dashboard';
import Navigation from 'navigation/NavigationConnector';
import SubscriptionsWizard from 'feature/subscription-plans/SubscriptionsContext';
import MainDashboard from 'feature/dashboard/Dashboard';
import Security from 'feature/security/Security.connector';
import Dispatching from 'feature/dispatching/Dispatching';
import { EvaluateFlag } from 'common/FeatureFlags/Flag';
import ProductOwnershipRoutes from './ProductOwnershipRoutes';
import LoadingRoutes from './LoadingRoutes';
import ReleaseNotesRoutes from './ReleaseNotesRoutes';
import Subscriptions from './SubscriptionRoutes.connector';
import ShippingRoutes from './ShippingRoutes';
import GlobalRoutes from './GlobalRoutes';
import App from '../App';
import PermissionsRolesGuard from './PermissionsRolesGuard';
import { AuthContext } from '../authentication/AuthContext';

const buildHomePath = (affiliates = []) => {
  const a =
    affiliates.find(x => x.hasActiveSubscription) ??
    affiliates.find(x => x.inventoryOwner) ??
    affiliates[0];

  if (!a) return '';

  const aff = String(a.currentAffiliateId ?? a.affiliateId ?? a.customerIdName ?? '');
  const comp = String(a.dataCompanyId ?? a.companyId ?? '');

  return aff ? `/affiliate/${aff}/${comp || '0'}` : '';
};

const Callback = () => {
  const navigate = useNavigate();
  const { affiliates } = useContext(AuthContext);

  useEffect(() => {
    (async () => {
      try {
        if (!R.isEmpty(isAuthenticated())) {
          navigate(buildHomePath(affiliates), { replace: true });
          return;
        }
        await signinRedirectCallback();
        const target = sessionStorage.getItem('post_login_redirect') || buildHomePath(affiliates);
        sessionStorage.removeItem('post_login_redirect');
        navigate(target, { replace: true });
      } catch {
        navigate(buildHomePath(affiliates), { replace: true });
      }
    })();
  }, [navigate, affiliates]);

  return <MAuthDialog message="Logging into Swift" />;
};

// const SilentRenew = () => {
//   signinSilentCallback();
//   return <span>Loading</span>;
// };

// This is the login page rendering
const PublicPage = () => {
  useEffect(() => {
    signinRedirect();
  }, []);
  return <MAuthDialog message="Setting up Swift" />;
};

// Page routes
const PublicRoutes = () => (
  <Routes>
    <Route element={<Callback />} path="/auth-callback" />
    <Route element={<PublicPage />} path="/" />
  </Routes>
);

// eslint-disable-next-line react/prop-types
const AppRoutes = () => {
  // eslint-disable-next-line react/prop-types
  const {
    isInventoryOwner,
    isProductOwnerView,
    isProductShippingManage,
    isProductShippingView,
    isAdmin,
  } = useUserRolePermissions();
  const { pathname } = useLocation();

  // This replaces the ScrollToTop component
  // when pathname changes scroll page to top
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return (
    <>
      <Navigation />
      <Routes>
        <Route element={<MainDashboard />} index />
        <Route element={<Hidden />} path="hidden" />
        {(EvaluateFlag(process.env.REACT_APP_LD_COMPONENT_PREVIEW) ||
          process.env.REACT_APP_BUILD_STATE === 'local') && (
          <Route element={<ComponentPreview />} path="component-preview/*" />
        )}
        <Route
          element={
            <PermissionsRolesGuard authCondition={isAdmin}>
              <Security />
            </PermissionsRolesGuard>
          }
          path="security"
        />
        <Route
          element={
            <SubscriptionsWizard>
              <Subscriptions />
            </SubscriptionsWizard>
          }
          path="swift-plus/*"
        />
        {EvaluateFlag(process.env.REACT_APP_LD_INVENTORY) && (
          <Route
            element={
              <PermissionsRolesGuard authCondition={isInventoryOwner || isProductOwnerView}>
                <ProductOwnershipRoutes />
              </PermissionsRolesGuard>
            }
            path="inventory-management/*"
          />
        )}
        {EvaluateFlag(process.env.REACT_APP_LD_LOADING) && (
          <Route element={<LoadingRoutes />} path="loading-management/*" />
        )}
        <Route element={<ReleaseNotesRoutes />} path="release-notes/*" />
        {EvaluateFlag(process.env.REACT_APP_LD_DISPATCHING) && (
          <Route element={<Dispatching />} path="dispatching/*" />
        )}
        {EvaluateFlag(process.env.REACT_APP_LD_GLOBAL) && (
          <Route element={<GlobalRoutes />} path="global-data/*" />
        )}
        {EvaluateFlag(process.env.REACT_APP_LD_SHIPPING) && (
          <Route
            element={
              <PermissionsRolesGuard
                authCondition={isInventoryOwner || isProductShippingManage || isProductShippingView}
              >
                <ShippingRoutes />
              </PermissionsRolesGuard>
            }
            path="shipping/*"
          />
        )}
      </Routes>
    </>
  );
};

const PrivateRoutes = props => {
  const { isAdmin, isUser } = useUserRolePermissions();
  const { affiliates } = useContext(AuthContext);

  if (!(affiliates && affiliates.length)) {
    return <MAuthDialog message="Loading Swift" />;
  }

  const homePath = buildHomePath(affiliates);

  return (
    <Routes>
      <Route element={<Callback />} path="/auth-callback" />
      <Route element={<App />} path="/">
        <Route element={<Navigate replace to={homePath} />} index />
        <Route
          element={
            <PermissionsRolesGuard authCondition={isUser}>
              <AppRoutes {...props} />
            </PermissionsRolesGuard>
          }
          path="affiliate/:currentAffiliateId/:dataCompanyId/*"
        />
        <Route
          element={
            <PermissionsRolesGuard authCondition={isAdmin && !isUser}>
              <Security />
            </PermissionsRolesGuard>
          }
          path=""
        />
        <Route element={<Navigate replace to={homePath} />} path="*" />
      </Route>
    </Routes>
  );
};

const AuthenticatedRoutes = props =>
  !R.isEmpty(isAuthenticated()) ? <PrivateRoutes {...props} /> : <PublicRoutes {...props} />;

export default AuthenticatedRoutes;
